import { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
  Badge,
  keyframes,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  ArrowBack,
  MoreVert,
  ExitToApp,
  Group,
  BugReport,
  PersonAdd,
  MenuBook,
  Chat,
  Settings,
} from '@mui/icons-material'
import { useSessionStore } from '../../stores/sessionStore'
import { useAbandonSession } from '../../hooks/useSession'
import SessionDebugPanel from './SessionDebugPanel'

const timerPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`

interface PlayerHeaderProps {
  title: string
  elapsed: number
  isGroup?: boolean
  participantCount?: number
  canDebug?: boolean
  onBack: () => void
  // Timer control
  isTimerPaused?: boolean
  // Adventure metadata
  adventureType?: string
  creatorName?: string
  // Group play
  isOwner?: boolean
  onInviteClick?: () => void
  // Session chat
  conversationId?: string | null
  // Session settings
  onSettingsClick?: () => void
}

// Format seconds to MM:SS or HH:MM:SS
function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

export default function PlayerHeader({
  title,
  elapsed,
  isGroup = false,
  participantCount = 1,
  canDebug = false,
  onBack,
  isTimerPaused = false,
  adventureType,
  creatorName,
  isOwner = false,
  onInviteClick,
  conversationId,
  onSettingsClick,
}: PlayerHeaderProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const store = useSessionStore()
  const abandonSession = useAbandonSession()

  // Compute unread journal count
  const latestSeq = store.latestJournalSequenceNumber
  const currentParticipant = store.participants.find(p => p.userId === store.sessionId) // fallback handled below
  const lastViewed = currentParticipant?.lastViewedJournalSequenceNumber ?? 0
  const unreadJournalCount = Math.max(0, latestSeq - lastViewed)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false)
  const [debugPanelOpen, setDebugPanelOpen] = useState(false)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleAbandonClick = () => {
    setAbandonDialogOpen(true)
    handleMenuClose()
  }

  const handleDebugClick = () => {
    setDebugPanelOpen(true)
    handleMenuClose()
  }

  const handleAbandonConfirm = async () => {
    if (store.sessionId) {
      await abandonSession.mutateAsync(store.sessionId)
      onBack()
    }
    setAbandonDialogOpen(false)
  }

  return (
    <>
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#16213e' : 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: isMobile ? 1 : 2,
          py: isMobile ? 0.75 : 1.5,
          flexShrink: 0,
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 0.75 : 1.5,
          }}
        >
          {/* Back button */}
          <IconButton
            onClick={onBack}
            edge="start"
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <ArrowBack fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>

          {/* Title + subtitle */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: isMobile ? '0.8125rem' : '0.9375rem',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.primary',
              }}
            >
              {title}
            </Typography>
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                {adventureType && (
                  <Box
                    sx={{
                      bgcolor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(212, 168, 83, 0.15)'
                        : 'rgba(212, 168, 83, 0.12)',
                      color: 'warning.main',
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 1,
                      fontSize: '0.625rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {adventureType}
                  </Box>
                )}
                {creatorName && (
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      color: 'text.disabled',
                    }}
                  >
                    by {creatorName}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.25 : 0.75 }}>
            {/* Invite button (group owner only) - hidden on mobile, moved to menu */}
            {!isMobile && isGroup && isOwner && onInviteClick && (
              <Tooltip title="Invite players">
                <IconButton
                  size="small"
                  onClick={onInviteClick}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <PersonAdd sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Journal toggle with unread badge */}
            <Tooltip title={`Journal${unreadJournalCount > 0 ? ` (${unreadJournalCount} unread)` : ''}`}>
              <IconButton
                size="small"
                onClick={() => store.toggleJournal()}
                sx={{
                  width: isMobile ? 28 : 32,
                  height: isMobile ? 28 : 32,
                  borderRadius: 2,
                  color: store.showJournal ? 'primary.main' : 'text.secondary',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <Badge
                  badgeContent={unreadJournalCount}
                  color="error"
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.6rem',
                      height: 14,
                      minWidth: 14,
                    },
                  }}
                >
                  <MenuBook sx={{ fontSize: isMobile ? 14 : 16 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Session chat button */}
            {conversationId && (
              <Tooltip title="Session Chat">
                <IconButton
                  size="small"
                  sx={{
                    width: isMobile ? 28 : 32,
                    height: isMobile ? 28 : 32,
                    borderRadius: 2,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <Chat sx={{ fontSize: isMobile ? 14 : 16 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Group indicator - hidden on mobile (GroupMembersBar shows this) */}
            {!isMobile && isGroup && (
              <Tooltip title={`${participantCount} players`}>
                <IconButton
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <Group sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Session settings gear icon (group sessions) */}
            {isGroup && onSettingsClick && (
              <Tooltip title="Session Settings">
                <IconButton
                  size="small"
                  onClick={onSettingsClick}
                  sx={{
                    width: isMobile ? 28 : 32,
                    height: isMobile ? 28 : 32,
                    borderRadius: 2,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <Settings sx={{ fontSize: isMobile ? 14 : 16 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Timer badge - teal pill with pulsing dot */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.625,
                bgcolor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(56, 178, 172, 0.12)'
                  : 'rgba(56, 178, 172, 0.08)',
                border: 1,
                borderColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(56, 178, 172, 0.25)'
                  : 'rgba(56, 178, 172, 0.2)',
                color: 'secondary.main',
                px: isMobile ? 0.75 : 1.25,
                py: isMobile ? 0.375 : 0.625,
                borderRadius: 5,
                fontSize: isMobile ? '0.6875rem' : '0.8125rem',
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {isTimerPaused ? (
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'warning.main' }}>
                  ⏸
                </Typography>
              ) : (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    bgcolor: 'secondary.main',
                    borderRadius: '50%',
                    animation: `${timerPulse} 2s infinite`,
                  }}
                />
              )}
              <span>{formatTime(elapsed)}</span>
            </Box>

            {/* Menu */}
            <IconButton
              onClick={handleMenuOpen}
              edge="end"
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
              }}
            >
              <MoreVert fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Invite option (mobile only - shown inline on desktop) */}
        {isMobile && isGroup && isOwner && onInviteClick && (
          <MenuItem onClick={() => { handleMenuClose(); onInviteClick() }}>
            <ListItemIcon>
              <PersonAdd fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Invite Players</ListItemText>
          </MenuItem>
        )}

        {canDebug && (
          <MenuItem onClick={handleDebugClick}>
            <ListItemIcon>
              <BugReport fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Debug Session</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleAbandonClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <ExitToApp fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Abandon Adventure</ListItemText>
        </MenuItem>
      </Menu>

      {/* Abandon confirmation dialog */}
      <Dialog open={abandonDialogOpen} onClose={() => setAbandonDialogOpen(false)}>
        <DialogTitle>Abandon Adventure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to abandon this adventure? Your progress will be lost
            and you will need to rejoin to start over.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbandonDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAbandonConfirm}
            color="error"
            disabled={abandonSession.isPending}
          >
            Abandon
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debug panel */}
      {canDebug && (
        <SessionDebugPanel
          open={debugPanelOpen}
          onClose={() => setDebugPanelOpen(false)}
        />
      )}
    </>
  )
}
